import { Card, CardHeader, CardContent } from '../ui/card';
import { Save, Check } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useProfessionalViewModel } from '../../view-model/professional/professional-view-model';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';

const LanguageSettings = observer(() => {
  const professionalViewModel = useProfessionalViewModel();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <Card className="flex-1 overflow-hidden flex flex-col">
      <CardHeader>
        <h2 className="text-lg font-medium">{t('profile.languageSettings', 'Paramètres de langue')}</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t('profile.languageDescription', 'Gérez les paramètres de langue de l\'application')}
        </p>
      </CardHeader>
      <CardContent className="flex-1 overflow-auto">
        <div className="space-y-6">
          <div>
            <h3 className="text-md font-medium mb-4">{t('profile.languageChoice', 'Langue de l\'application')}</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              {t('profile.languageDescription', 'Sélectionnez la langue dans laquelle vous souhaitez utiliser l\'application.')}
            </p>

            <div className="space-y-2">
              <div
                onClick={() => {
                  if (professionalViewModel.isUpdate) {
                    // Extraire le préfixe de langue actuel
                    const currentPath = location.pathname;
                    const pathWithoutLang = currentPath.replace(/^\/[a-z]{2}(\/|$)/, '/');
                    
                    // Construire le nouveau chemin avec le nouveau préfixe de langue
                    const newPath = `/fr${pathWithoutLang === '/' ? '' : pathWithoutLang}`;
                    
                    // Préserver les paramètres d'URL existants
                    const searchParams = new URLSearchParams(location.search);
                    const queryString = searchParams.toString();
                    const fullPath = queryString ? `${newPath}?${queryString}` : newPath;
                    
                    professionalViewModel.setLanguage('fr');
                    i18n.changeLanguage('fr');
                    
                    // Naviguer vers la nouvelle URL avec le préfixe de langue et les paramètres
                    navigate(fullPath);
                  }
                }}
                className={`flex items-center justify-between p-3 ${professionalViewModel.isUpdate ? 'hover:bg-gray-50 dark:hover:bg-gray-750 cursor-pointer' : ''} rounded-lg border border-gray-200 dark:border-gray-700 transition-colors ${professionalViewModel.language === 'fr' ? 'bg-gray-50 dark:bg-gray-750' : ''}`}>
                <div className="flex items-center">
                  <div className="w-8 h-8 rounded-full overflow-hidden mr-3">
                    <img src="/flags/fr.svg" alt="Drapeau français"
                         className="w-full h-full object-cover"/>
                  </div>
                  <div>
                    <p className="font-medium">{t('profile.languageFR', 'Français')}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{t('profile.languageFR', 'Français (France)')}</p>
                  </div>
                </div>
                {professionalViewModel.language === 'fr' && (
                  <div
                    className="w-6 h-6 rounded-full bg-primary flex items-center justify-center text-white">
                    <Check size={14}/>
                  </div>
                )}
              </div>

              <div
                onClick={() => {
                  if (professionalViewModel.isUpdate) {
                    // Extraire le préfixe de langue actuel
                    const currentPath = location.pathname;
                    const pathWithoutLang = currentPath.replace(/^\/[a-z]{2}(\/|$)/, '/');
                    
                    // Construire le nouveau chemin avec le nouveau préfixe de langue
                    const newPath = `/en${pathWithoutLang === '/' ? '' : pathWithoutLang}`;
                    
                    // Préserver les paramètres d'URL existants
                    const searchParams = new URLSearchParams(location.search);
                    const queryString = searchParams.toString();
                    const fullPath = queryString ? `${newPath}?${queryString}` : newPath;
                    
                    professionalViewModel.setLanguage('en');
                    i18n.changeLanguage('en');
                    
                    // Naviguer vers la nouvelle URL avec le préfixe de langue et les paramètres
                    navigate(fullPath);
                  }
                }}
                className={`flex items-center justify-between p-3 ${professionalViewModel.isUpdate ? 'hover:bg-gray-50 dark:hover:bg-gray-750 cursor-pointer' : ''} rounded-lg border border-gray-200 dark:border-gray-700 transition-colors ${professionalViewModel.language === 'en' ? 'bg-gray-50 dark:bg-gray-750' : ''}`}>
                <div className="flex items-center">
                  <div className="w-8 h-8 rounded-full overflow-hidden mr-3">
                    <img src="/flags/gb.svg" alt="Drapeau anglais"
                         className="w-full h-full object-cover"/>
                  </div>
                  <div>
                    <p className="font-medium">{t('profile.languageEN', 'English')}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{t('profile.languageEN', 'English (United Kingdom)')}</p>
                  </div>
                </div>
                {professionalViewModel.language === 'en' && (
                  <div
                    className="w-6 h-6 rounded-full bg-primary flex items-center justify-center text-white">
                    <Check size={14}/>
                  </div>
                )}
              </div>
            </div>
          </div>

          {professionalViewModel.isUpdate && (
            <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={(e) => professionalViewModel.handleUpdateProfileProfessional(e)}
                className="px-4 py-2 bg-primary hover:bg-primary-dark text-white rounded-lg text-sm font-medium transition-colors"
                disabled={professionalViewModel.isLoading}
              >
                {professionalViewModel.isLoading ? (
                  <>
                    <span className="animate-spin inline-block mr-2">⏳</span>
                    {t('profile.updatePreferences', 'Mise à jour...')}
                  </>
                ) : (
                  <>
                    <Save size={16} className="mr-2 inline-block"/>
                    {t('profile.savePreferences', 'Enregistrer les préférences')}
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
});

export default LanguageSettings;
